import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import taskRequest from "@/api/task-request";
import type { StatementTaskAction } from "@/types/task-actions";
import { openPortalFile } from "@/utils/portalFile";
import { formatStatementDate, usd } from "@/utils/statement";
import type { StatementPaymentResult } from "@/api/statement-payment-request";
import { StatementPaymentForm } from "@/components/statement/StatementPaymentForm";

/**
 * A statement from a STMT portal task (/menu/statement?taskId=N). Loaded from the API on
 * every visit so a refresh or patient switch never shows another patient's statement.
 */
export function StatementPage() {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const [searchParams] = useSearchParams();
  const taskId = Number(searchParams.get("taskId"));
  const [statement, setStatement] = useState<StatementTaskAction | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [paid, setPaid] = useState<StatementPaymentResult | null>(null);

  useEffect(() => {
    setStatement(null);
    setFailed(false);
    setPaid(null);
    const facilityKey = getFacilityKeyForPortal();
    const patientKey = getPatientKey();
    if (!currentPatient || !facilityKey || !patientKey || !(taskId > 0)) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    taskRequest.request
      .getStatementTaskAction(facilityKey, patientKey, taskId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        if (!data || data.error || !data.fileID) setFailed(true);
        else setStatement(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentPatient, taskId]);

  const handleView = async () => {
    if (!currentPatient || !statement) return;
    setOpening(true);
    try {
      await openPortalFile({
        facilityId: currentPatient.facilityID,
        fileId: statement.fileID,
        expectedName: "statement.pdf",
      });
    } catch {
      toast.error("We couldn't open your statement. Please try again.");
    } finally {
      setOpening(false);
    }
  };

  if (!currentPatient) return null;

  const statementDate = formatStatementDate(statement?.statementDate);
  const dueDate = formatStatementDate(statement?.pmtDueDate);
  const totalDue = statement?.totalDue ?? 0;
  const balanceDue =
    paid && paid.posted !== false ? paid.balanceDue : (statement?.balanceDue ?? totalDue);

  return (
    <div className="flex flex-col gap-3">
      <header className="flex shrink-0 items-center">
        <div className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy" />
        <h2 className="text-xl font-extrabold text-healthsource-dark">Statement</h2>
      </header>

      <div className="sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]">
        {loading && (
          <div className="py-6 text-center text-sm text-healthsource-gray">
            Loading statement…
          </div>
        )}

        {!loading && failed && (
          <div className="flex flex-col items-center gap-3 py-6 text-center text-sm text-healthsource-gray">
            <p>This statement isn't available.</p>
            <Link to="/" className="font-bold text-healthsource-navy underline">
              Back to dashboard
            </Link>
          </div>
        )}

        {!loading && statement && (
          <div className="sm:p-5">
            <div
              className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender bg-white"
              style={{ boxShadow: "0 2px 8px rgba(30,37,43,0.06)" }}
            >
              <div className="w-1 flex-shrink-0 rounded-r-sm bg-healthsource-navy" />
              <div className="min-w-0 flex-1 px-4 py-5 sm:pl-5">
                <p className="text-[13px] font-bold uppercase tracking-wide text-healthsource-gray">
                  {balanceDue > 0 ? "Amount due" : "Balance"}
                </p>
                <p className="mt-1 text-3xl font-extrabold text-healthsource-dark">
                  {usd.format(balanceDue)}
                </p>
                {balanceDue !== totalDue && (
                  <p className="mt-1 text-[13px] text-healthsource-gray">
                    Statement total {usd.format(totalDue)}
                  </p>
                )}

                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  {statementDate && (
                    <div>
                      <dt className="text-healthsource-gray">Statement date</dt>
                      <dd className="font-bold text-healthsource-dark">{statementDate}</dd>
                    </div>
                  )}
                  {dueDate && (
                    <div>
                      <dt className="text-healthsource-gray">Payment due</dt>
                      <dd className="font-bold text-healthsource-dark">{dueDate}</dd>
                    </div>
                  )}
                </dl>

                <button
                  type="button"
                  onClick={handleView}
                  disabled={opening}
                  className="mt-5 flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-healthsource-navy px-4 py-2 text-[14px] font-bold text-healthsource-navy transition-colors hover:bg-healthsource-navy/5 disabled:opacity-60 sm:w-auto"
                >
                  <i className="far fa-file-pdf" aria-hidden />
                  {opening ? "Opening…" : "View statement"}
                </button>

                {paid ? (
                  <div role="status" className="mt-5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    <p className="font-bold">Payment received: {usd.format(paid.amountPaid)}</p>
                    <p className="mt-1">
                      {paid.posted === false
                        ? "The office will apply it to your statement."
                        : paid.balanceDue > 0
                        ? `${usd.format(paid.balanceDue)} is still due on this statement.`
                        : "This statement is paid in full."}
                      {paid.receiptEmailed ? " Your receipt is on its way by email." : ""}
                      {paid.cardSaved ? " Your card is saved for future payments." : ""}
                    </p>
                  </div>
                ) : balanceDue <= 0 ? (
                  <p className="mt-4 text-[13px] font-bold text-healthsource-gray">
                    This statement is paid in full.
                  </p>
                ) : statement.canPayOnline ? (
                  <StatementPaymentForm taskId={taskId} balanceDue={balanceDue} onPaid={setPaid} />
                ) : (
                  <p className="mt-4 text-[13px] text-healthsource-gray">
                    To pay this balance, please contact your clinic.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
